import {
    analyticsElements,
    IAnalyticsTag,
    IAnalyticsAction,
} from './analyticsElements';
import { getTagValue, IAnalyticsTagValue } from './analyticsTagParsers';
const ACCEPTED_SELECTORS = analyticsElements;

const ENTER_KEY_CODE = '13';
const SPACEBAR_KEY_CODE = '32';

interface cafeData {
    analyticsURL: string;
    analyticsApiKey: string;
}

let eventQueue: any = [];
let defaultTags: any = {};
let roleTag: IAnalyticsTagValue;

export const getEventQueue = () => {
    return eventQueue;
};

export const resetEventQueue = () => {
    eventQueue = [];
};

// mandated by CAFe documentation
// https://wiki.cengage.com/display/cap/Client-Side+Eventing+Timestamp+Requirements
const getLocalDateISOString = () => {
    const now = new Date();
    const tzo = now.getTimezoneOffset();
    const pad = (num, len) => {
        const norm = Math.abs(Math.floor(num)).toString();
        if (norm.length > len) {
            return norm;
        }

        let padding = '';
        for (let i = 0; i < len - norm.length; i++) {
            padding += '0';
        }
        return padding + norm;
    };
    return now
        .getFullYear()
        .toString()
        .concat(
            '-',
            pad(now.getMonth() + 1, 2),
            '-',
            pad(now.getDate(), 2),
            'T',
            pad(now.getHours(), 2),
            ':',
            pad(now.getMinutes(), 2),
            ':',
            pad(now.getSeconds(), 2),
            '.',
            pad(now.getMilliseconds(), 3),
            tzo < 0 ? '+' : '-',
            pad(Math.abs(tzo) / 60, 2),
            ':',
            pad(Math.abs(tzo) % 60, 2),
        );
};

const getEventId = () => {
    var dt = new Date().getTime();
    var uid = 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'.replace(/[x]/g, function(c) {
        var r = (dt + Math.random() * 16) % 16 | 0;
        dt = Math.floor(dt / 16);
        return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
    return uid;
};

const addEnvironmentAndPlatform = () => {
    const host = window.location.host;

    // Environment specific variables
    // The WebAssign environment
    let productEnvironment = 'develop';
    // The SSO environment
    let userEnvironment = 'integration';
    // The Identity Management system that stores the users
    let userPlatform = 'sso';

    if (host.includes('www.webassign.net')) {
        userEnvironment = 'production';
        productEnvironment = 'production';
    } else if (host.includes('demo')) {
        userEnvironment = 'demo';
        productEnvironment = 'demo';
        userPlatform = 'webassign';
    } else if (host.includes('editorial')) {
        userEnvironment = 'editorial';
        productEnvironment = 'editorial';
        userPlatform = 'webassign';
    } else if (host.includes('perf')) {
        userEnvironment = 'performance';
        productEnvironment = 'performance';
    } else if (host.includes('staging')) {
        userEnvironment = 'qa';
        productEnvironment = 'staging';
    } else if (host.includes('integration')) {
        userEnvironment = 'integration';
        productEnvironment = 'integration';
    }

    // Do not include the query string to ensure no PII is included in the URL
    const eventUri = [
        location.protocol,
        '//',
        location.host,
        location.pathname,
    ].join('');

    return {
        eventUri,
        productEnvironment,
        userEnvironment,
    };
};

export const analyticsWatcher = (() => {
    const getValuesForTags = (
        tags: IAnalyticsTag[],
        element: HTMLElement = null,
    ): IAnalyticsTagValue[] => {
        const values: IAnalyticsTagValue[] = [];
        if (tags) {
            for (let i = 0; i < tags.length; i++) {
                const value: IAnalyticsTagValue = getTagValue(
                    tags[i],
                    element as HTMLElement,
                );
                if (value !== null) {
                    values.push(value);
                }
            }
        }
        return values;
    };

    const eventHandler = (
        selector: IAnalyticsAction,
        element: HTMLElement = null,
    ) => {
        const { eventAction, eventCategory, tags } = selector;

        const eventData = {
            eventAction,
            eventCategory,
            eventId: getEventId(),
            eventTime: getLocalDateISOString(),
        };

        let eventTags = [];

        if (tags && tags.length > 0) {
            // Collect values from elements for tags specific to few actions
            // ex: sectionId, assignmentId ...
            const selectorSpecificTags = getValuesForTags(tags, element);

            if (selectorSpecificTags.length > 0) {
                eventTags = selectorSpecificTags;
            }
        }

        if (roleTag) {
            eventTags.unshift(roleTag);
        }
        // Get tags from element like <input data-analytics-tags="{'courseId':1234}" />
        if (element) {
            const tagsFromElement = element.dataset['analyticsTags'];

            if (tagsFromElement) {
                const tagsObject = JSON.parse(tagsFromElement);
                Object.keys(tagsObject).forEach(key => {
                    eventTags.push({ key, value: tagsObject[key] });
                });
            }
        }

        if (eventTags.length > 0) {
            eventData['tags'] = eventTags;
        }

        eventQueue.push({ ...defaultTags, ...eventData });
    };

    const isElementButton = element => {
        const isButtonTag = element.tagName.toLowerCase() === 'button';
        const isLinkTag = element.tagName.toLowerCase() === 'a';
        const isRoleButton = element.getAttribute('role') === 'button';
        const isTypeButton = element.getAttribute('type') === 'button';
        const isRoleLink = element.getAttribute('role') === 'link';
        const isInputTag = element.tagName.toLowerCase() === 'input';
        const isCheckBox = element.getAttribute('type') === 'checkbox';

        return (
            (isButtonTag && !isRoleLink) ||
            (isLinkTag && isRoleButton) ||
            (isInputTag && isCheckBox) ||
            (isInputTag && isTypeButton)
        );
    };

    const isElementLink = element => {
        const isButtonTag = element.tagName.toLowerCase() === 'button';
        const isLinkTag = element.tagName.toLowerCase() === 'a';
        const isRoleButton = element.getAttribute('role') === 'button';
        const isRoleLink = element.getAttribute('role') === 'link';

        return (isLinkTag && !isRoleButton) || (isButtonTag && isRoleLink);
    };

    let clickListeners = [];
    let keydownListeners = [];

    const addLinkListeners = (element, selector) => {
        const clickListener = {
            element,
            handler: event => {
                eventHandler(selector, event.currentTarget);
            },
        };
        const keydownListener = {
            element,
            handler: event => {
                if (event.code === ENTER_KEY_CODE) {
                    eventHandler(selector, event.currentTarget);
                }
            },
        };

        clickListeners.push(clickListener);
        keydownListeners.push(keydownListener);
        element.addEventListener('click', clickListener.handler);
        element.addEventListener('keydown', keydownListener.handler);
    };

    const addButtonListeners = (element, selector) => {
        const clickListener = {
            element,
            handler: event => {
                eventHandler(selector, event.currentTarget);
            },
        };
        const keydownListener = {
            element,
            handler: event => {
                if (
                    event.code === ENTER_KEY_CODE ||
                    event.code === SPACEBAR_KEY_CODE
                ) {
                    eventHandler(selector, event.currentTarget);
                }
            },
        };

        clickListeners.push(clickListener);
        keydownListeners.push(keydownListener);
        element.addEventListener('click', clickListener.handler);
        element.addEventListener('keydown', keydownListener.handler);
    };

    const removeListeners = () => {
        clickListeners.forEach(({ element, handler }) => {
            element.removeEventListener('click', handler);
        });
        keydownListeners.forEach(({ element, handler }) => {
            element.removeEventListener('keydown', handler);
        });
        clickListeners = [];
        keydownListeners = [];
    };

    const setDefaultTags = () => {
        const userSSOGUIDelement = document.getElementById(
            'wa-analytics-userSSOGUID',
        ) as HTMLInputElement;
        const sessionIdElement = document.getElementById(
            'wa-analytics-sessionId',
        ) as HTMLInputElement;
        const userRoleElement = document.getElementById(
            'wa-analytics-role',
        ) as HTMLInputElement;
        const {
            eventUri,
            productEnvironment,
            userEnvironment,
        } = addEnvironmentAndPlatform();

        if (userRoleElement) {
            roleTag = {
                key: 'role',
                value: userRoleElement.value,
            };
        }

        defaultTags = {
            productEnvironment,
            eventUri,
            messageFormatVersion: 1,
            messageType: 'ClientEventingActivity',
            productPlatform: 'webassign',
            sessionId: sessionIdElement ? sessionIdElement.value : '',
            userEnvironment,
            userPlatform: 'sso',
            userSSOGUID: userSSOGUIDelement ? userSSOGUIDelement.value : '',
        };
    };

    const isChromeVersionGreaterThan = (version: number): boolean => {
        const chromeInfo = navigator.userAgent.match(
            /Chrom(?:e|ium)\/([0-9]+)\./,
        );
        if (!chromeInfo) {
            return false;
        }
        return parseInt(chromeInfo[1], 10) > version;
    };

    const isFirefoxVersionGreaterThan = (version: number): boolean => {
        const mozInfo = navigator.userAgent.match(/Firefox\/([0-9]+)\./);
        if (!mozInfo) {
            return false;
        }
        return parseInt(mozInfo[1], 10) > version;
    };

    const isSafari = (): boolean =>
        navigator.userAgent.indexOf('Safari') != -1 &&
        navigator.userAgent.indexOf('Chrome') == -1;

    const getCafeData = (): cafeData => {
        const urlElement = document.getElementById(
            'wa-analytics-URL',
        ) as HTMLInputElement;
        const apiElement = document.getElementById(
            'wa-analytics-API',
        ) as HTMLInputElement;

        const analyticsURL = urlElement ? urlElement.value : '';
        const analyticsApiKey = apiElement ? apiElement.value : '';

        return {
            analyticsURL,
            analyticsApiKey,
        };
    };

    const xhrHandler = (): void => {
        const { analyticsURL, analyticsApiKey } = getCafeData();

        if (!analyticsURL || !analyticsApiKey) {
            return;
        }

        if (eventQueue.length < 1) {
            return;
        }

        const xhr = new XMLHttpRequest();
        xhr.open('POST', analyticsURL, false);
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
        xhr.setRequestHeader('x-api-key', analyticsApiKey);
        const records = JSON.stringify({ records: eventQueue });
        xhr.send(records);
    };

    const fetchHandler = (): void => {
        const { analyticsURL, analyticsApiKey } = getCafeData();

        if (!analyticsURL || !analyticsApiKey) {
            return;
        }

        if (eventQueue.length < 1) {
            return;
        }

        fetch(analyticsURL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json;charset=UTF-8',
                'x-api-key': analyticsApiKey,
                accept: 'application/json',
            },
            body: JSON.stringify({ records: eventQueue }),
            mode: 'cors',
            cache: 'no-cache',
            keepalive: true,
        });
    };

    const setListeners = () => {
        /**** Interaction Handling ****/
        ACCEPTED_SELECTORS.forEach((selector: any) => {
            const elements = document.querySelectorAll(selector.selector);
            const elementsLength = elements.length;
            for (let i = 0; i < elementsLength; i++) {
                if (isElementButton(elements[i])) {
                    addButtonListeners(elements[i], selector);
                } else if (isElementLink(elements[i])) {
                    addLinkListeners(elements[i], selector);
                } else {
                    eventHandler(selector, elements[i]);
                }
            }
        });
    };

    document.addEventListener('DOMContentLoaded', () => {
        window.setTimeout(() => {
            setDefaultTags();
            setListeners();
        });
    });

    document.addEventListener('set_analytics_listeners', () => {
        window.setTimeout(() => {
            removeListeners();
            setListeners();
        });
    });

    if (isChromeVersionGreaterThan(80) || isFirefoxVersionGreaterThan(76)) {
        window.addEventListener('unload', fetchHandler);
    } else if (isSafari()) {
        window.addEventListener('beforeunload', fetchHandler);
    } else {
        window.addEventListener('unload', xhrHandler);
    }
})();
